/**
 * GuestRegistrationBanner - Persistent CTA for guest users to register
 * Shows options to register with Google, Apple (on iOS) or email/password
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui';
import toast from 'react-hot-toast';

interface GuestRegistrationBannerProps {
  variant?: 'full' | 'compact';
}

export function GuestRegistrationBanner({ variant = 'full' }: GuestRegistrationBannerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customClaims, upgradeGuestAccount, upgradeGuestAccountWithApple } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Check if we're on iOS native (Apple Sign-In available)
  // Use multiple detection methods since Capacitor might not work correctly with remote URLs
  const isCapacitorIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  const isIOSUserAgent = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isCapacitorWebView = !!(window as any).Capacitor;
  const isAppleDevice = isCapacitorIOS || (isIOSUserAgent && isCapacitorWebView);

  // Only show for guest users
  if (customClaims?.role !== 'guest' || dismissed) {
    return null;
  }

  const handleGoogleUpgrade = async () => {
    setGoogleLoading(true);
    try {
      await upgradeGuestAccount();
      toast.success(t('guest.upgradeSuccess'));
    } catch (error: any) {
      console.error('Error upgrading guest account:', error);
      if (error.code === 'auth/credential-already-in-use') {
        toast.error(t('guest.googleAlreadyLinked'));
      } else {
        toast.error(error.message || t('guest.upgradeError'));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleUpgrade = async () => {
    setAppleLoading(true);
    try {
      await upgradeGuestAccountWithApple();
      toast.success(t('guest.upgradeSuccess'));
    } catch (error: any) {
      console.error('Error upgrading guest account with Apple:', error);
      // Don't show error for user cancellation
      if (error.message?.includes('cancelado') || error.message?.includes('cancelled')) {
        // User cancelled, do nothing
      } else if (error.code === 'auth/credential-already-in-use') {
        toast.error(t('guest.appleAlreadyLinked', 'Esta cuenta de Apple ya está vinculada a otro usuario'));
      } else {
        toast.error(error.message || t('guest.upgradeError'));
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const loading = googleLoading || appleLoading;

  const handleEmailRegister = () => {
    navigate('/profile', { state: { openRegister: true } });
  };

  if (variant === 'compact') {
    return (
      <div className="bg-surface border-b border-hairline text-ink px-3 py-2">
        <div className="flex items-center justify-between gap-2 max-w-7xl mx-auto">
          <span className="text-sm font-medium text-ink-muted truncate">
            {t('guest.bannerCompact')}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Apple Sign-In button - show on iOS */}
            {isAppleDevice && (
              <button
                onClick={handleAppleUpgrade}
                disabled={loading}
                className="flex items-center gap-1 px-3 py-1.5 bg-surface-3 text-ink border border-hairline rounded-full text-sm font-medium hover:bg-surface-2 transition-colors disabled:opacity-50"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                  />
                </svg>
                <span className="hidden sm:inline">Apple</span>
              </button>
            )}
            <button
              onClick={handleGoogleUpgrade}
              disabled={loading}
              className="flex items-center gap-1 px-3 py-1.5 bg-surface-3 text-ink border border-hairline rounded-full text-sm font-medium hover:bg-surface-2 transition-colors disabled:opacity-50"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="hidden sm:inline">Google</span>
            </button>
            <button
              onClick={handleEmailRegister}
              className="px-3 py-1.5 bg-surface-3 text-ink border border-hairline rounded-full text-sm font-medium hover:bg-surface-2 transition-colors"
            >
              {t('guest.email')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border-b border-hairline text-ink">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Message */}
          <div className="flex items-center gap-3 text-center sm:text-left">
            <div className="hidden sm:flex w-10 h-10 bg-surface-3 text-ink-subtle rounded-full items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <p className="font-semibold text-sm sm:text-base text-ink">
                {t('guest.bannerTitle')}
              </p>
              <p className="text-ink-muted text-xs sm:text-sm">
                {t('guest.bannerSubtitle')}
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Apple Sign-In button - show on iOS */}
            {isAppleDevice && (
              <Button
                onClick={handleAppleUpgrade}
                isLoading={appleLoading}
                disabled={loading && !appleLoading}
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                  />
                </svg>
                <span className="hidden xs:inline">{t('guest.continueWithApple', 'Continuar con Apple')}</span>
                <span className="xs:hidden">Apple</span>
              </Button>
            )}

            <Button
              onClick={handleGoogleUpgrade}
              isLoading={googleLoading}
              disabled={loading && !googleLoading}
              variant="secondary"
              size="sm"
              className="flex-1 sm:flex-none"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="hidden xs:inline">{t('guest.continueWithGoogle')}</span>
              <span className="xs:hidden">Google</span>
            </Button>

            <Button
              onClick={handleEmailRegister}
              disabled={loading}
              variant="ghost"
              size="sm"
              className="flex-1 sm:flex-none border border-hairline"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="hidden xs:inline">{t('guest.createAccount')}</span>
              <span className="xs:hidden">{t('guest.email')}</span>
            </Button>

            {/* Dismiss button */}
            <button
              onClick={() => setDismissed(true)}
              className="p-1.5 text-ink-subtle hover:text-ink hover:bg-surface-2 rounded-full transition-colors flex-shrink-0"
              title={t('common.close')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
